/**
 * Phase F-finale-7 Slice 7b.3 — SA cross-tenant seat-assignment-config API client.
 *
 * Wraps the super-admin, cross-tenant BE surface added in Slice 7b.2
 * (eduviaos-backend PR #158):
 *
 *   GET    /super-admin/tenants/{slug}/seating/config
 *   PUT    /super-admin/tenants/{slug}/seating/config
 *   POST   /super-admin/tenants/{slug}/seating/config/reset
 *
 * The target tenant + its vertical are resolved BE-side from the path slug
 * (404 when the tenant doesn't exist) and the actor is the super-admin from
 * the JWT — none of that is client-trusted. Guarded by the `super_admin`
 * role server-side. The response shape is identical to the tenant-self
 * surface (`SeatAssignmentConfigResponse` — no `tenant_slug`, since the slug
 * is in the URL), so these wire types mirror the IA Slice 7b.1 ones exactly.
 *
 * `fixed_assignment_method` + `fixed_assignment_order` are only valid when
 * `mode === "fixed"`; the BE 422s any other combination and `buildWriteBody`
 * normalises the draft so the UX never sends an invalid pairing.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type SeatAssignmentMode = "none" | "zone_only" | "dynamic" | "fixed" | "mixed";

/** Plan a `mixed` centre applies to a learner who has no plan of their own. */
export type MixedDefaultPlan = "first_come" | "flexible_hours" | "no_seat";

/** "Who assigns" axis — only meaningful when `mode === "fixed"`. */
export type FixedAssignmentMethod = "admin_assigns" | "student_picks" | "auto_round_robin";

/** "Ordering basis" axis — only meaningful when `mode === "fixed"`. */
export type FixedAssignmentOrder = "alphabetical" | "merit" | "manual";

export type SeatAssignmentSource = "tenant" | "vertical_default";

export type SeatAssignmentVertical = "school" | "coaching_institute" | "study_centre";

/** Canonical `SeatAssignmentPayload` fields (PUT body shape). */
export interface SeatAssignmentConfigBody {
  mode: SeatAssignmentMode;
  require_booking: boolean;
  allow_seat_switch: boolean;
  allow_session_extension: boolean;
  fixed_assignment_method: FixedAssignmentMethod | null;
  fixed_assignment_order: FixedAssignmentOrder | null;
  enforce_zone_capacity: boolean;
  waitlist_enabled: boolean;
  /** Whole-hour bookings beside the named shifts. Charge is hours times the rate. */
  custom_duration_enabled: boolean;
  /** Price of one hour, in paise. Must be above zero while custom hours are on. */
  custom_hourly_rate_paise: number;
  /** Only read in `mixed` mode. Always sent back on PUT so a save never resets it. */
  mixed_default_plan?: MixedDefaultPlan;
}

/** GET / PUT response — payload + provenance. */
export interface SeatAssignmentConfigResponse extends SeatAssignmentConfigBody {
  source: SeatAssignmentSource;
  vertical: SeatAssignmentVertical;
}

export type WriteSeatAssignmentConfigRequest = SeatAssignmentConfigBody;

// ── React Query hooks ─────────────────────────────────────────────────

/** Cache-key for a tenant's resolved seat-assignment config. */
export function seatingConfigKey(tenantSlug: string) {
  return ["super-admin", "seating-config", tenantSlug] as const;
}

const base = (slug: string) => `/super-admin/tenants/${encodeURIComponent(slug)}/seating/config`;

export function useSeatConfig(tenantSlug: string) {
  return useQuery<SeatAssignmentConfigResponse>({
    queryKey: seatingConfigKey(tenantSlug),
    queryFn: () => api.get<SeatAssignmentConfigResponse>(base(tenantSlug)),
    enabled: Boolean(tenantSlug),
    staleTime: 15_000,
  });
}

export function useWriteSeatConfig(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation<SeatAssignmentConfigResponse, ApiError, WriteSeatAssignmentConfigRequest>({
    mutationFn: (body) => api.put<SeatAssignmentConfigResponse>(base(tenantSlug), body),
    onSuccess: (data) => {
      qc.setQueryData(seatingConfigKey(tenantSlug), data);
    },
  });
}

export function useResetSeatConfig(tenantSlug: string) {
  const qc = useQueryClient();
  return useMutation<SeatAssignmentConfigResponse, ApiError, void>({
    mutationFn: () =>
      api.post<SeatAssignmentConfigResponse>(`${base(tenantSlug)}/reset`, undefined),
    onSuccess: (data) => {
      qc.setQueryData(seatingConfigKey(tenantSlug), data);
    },
  });
}

// ── Pure helpers (UI labels + write-body normalisation) ────────────────

export const SEAT_MODE_LABELS: Record<SeatAssignmentMode, string> = {
  none: "No seating",
  zone_only: "Zone only",
  dynamic: "Dynamic (per-session)",
  fixed: "Fixed (permanent)",
  mixed: "Mixed (per-learner plans)",
};

export const SEAT_MODE_HELP: Record<SeatAssignmentMode, string> = {
  none: "Members simply check in — no zones, seats, or bookings are tracked.",
  zone_only: "Members pick a zone (floor / room / section); individual seats are not tracked.",
  dynamic: "Seats are booked per session and freed afterwards — the everyday study-centre flow.",
  fixed: "Each member is allocated a permanent seat that stays theirs across sessions.",
  mixed:
    "Each learner follows their own seat plan, set by the centre. Learners without one use the centre's default plan.",
};

export const FIXED_METHOD_LABELS: Record<FixedAssignmentMethod, string> = {
  admin_assigns: "Admin assigns",
  student_picks: "Student picks",
  auto_round_robin: "Auto (round-robin)",
};

export const FIXED_ORDER_LABELS: Record<FixedAssignmentOrder, string> = {
  alphabetical: "Alphabetical",
  merit: "By merit",
  manual: "Manual order",
};

/**
 * Normalise a draft into a valid PUT body. The BE rejects a non-null
 * `fixed_assignment_method` / `fixed_assignment_order` when `mode !==
 * "fixed"` (422), so we strip them client-side; conversely, entering
 * `fixed` mode defaults both axes so the body always satisfies the
 * cross-field invariant.
 */
export function buildWriteBody(draft: SeatAssignmentConfigBody): WriteSeatAssignmentConfigRequest {
  if (draft.mode === "fixed") {
    return {
      ...draft,
      fixed_assignment_method: draft.fixed_assignment_method ?? "admin_assigns",
      fixed_assignment_order: draft.fixed_assignment_order ?? "alphabetical",
    };
  }
  return {
    ...draft,
    fixed_assignment_method: null,
    fixed_assignment_order: null,
  };
}
